import { useState, useCallback, useEffect } from "react";
import {
  Card,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Chip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "./LayoutContainer/DashboardLayout";
import MDBox from "../components/MDBox";
import MDTypography from "../components/MDTypography";
import SearchField from "../components/SearchField";
import Pagination from "../components/Pagination";

import SelectField from "../components/SelectField";
import { useAuth } from "../AuthProvider";
import { axios } from "../common";

import { useTranslation } from "react-i18next";

const DEFAULT_ITEMS_PER_PAGE = 10;
const DEFAULT_PAGE = 1;

const _ = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectType, setSelectType] = useState("all");
  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState();
  const [itemsPerPage, setItemsPerPage] = useState();
  const [activePage, setActivePage] = useState();
  const [keyword, setKeyword] = useState();

  const [totalCount, setTotalCount] = useState(0);
  const [normalCount, setNormalCount] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);

  const changePaginations = useCallback(
    (type, payload) => {
      if (!itemsPerPage) {
        return;
      }

      const params = new URLSearchParams(location.search);

      const i = type === "itemsPerPage" ? payload : itemsPerPage;

      const p = type === "activePage" ? payload : activePage;

      params.set("items_per_page", `${i}`);
      params.set("page", `${p}`);

      if (keyword) {
        params.set("keyword", keyword);
      } else {
        params.delete("keyword");
      }

      const qs = `?${params.toString()}`;

      if (location.search !== qs) {
        navigate(qs, {
          replace: !!location.search,
        });
      }
    },
    [itemsPerPage, activePage, keyword, location.search, navigate],
  );

  const getAlarmRows = (station) => {
    const alarms = station.alaramCodes || [];

    const grouped = {};

    alarms.forEach((alarm) => {
      const key = alarm.invNo;

      if (!grouped[key]) {
        grouped[key] = {
          invNo: alarm.invNo,
          invOrder: alarm.invOrder,
          alarms: [],
        };
      }

      grouped[key].alarms.push(alarm);
    });

    return Object.values(grouped);
  };

  const loadItems = useCallback(
    async (page, keyword = "") => {
      if (!page) return;

      setIsPending(true);

      try {
        const { status, data } = await axios.get("/station/dashboardcenter", {
          params: {
            cusNo: user?.id,
            page: page,
            pageSize: itemsPerPage,
            filter: selectType,
            keyword: keyword,
          },
        });

        if (status === 200) {
          setData(data.data.dashboardData);
          setTotalCount(data.data.dashboardData.totalItemCount);
          setNormalCount(data.data.stanbyCount + data.data.runingCount);
          setWarningCount(data.data.warningCount);
          setOfflineCount(data.data.offlineCount);
          setIsPending(false);
        }
      } catch (error) {
        setTimeout(() => {
          setIsPending(false);
        }, 1);
      }
    },
    [itemsPerPage, user?.id, selectType],
  );

  /**
   * 최초 진입 또는 URL 변경 시
   * URL → State
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const urlPage = Number(params.get("page"));
    const urlItemsPerPage = Number(params.get("items_per_page"));
    const urlKeyword = params.get("keyword") || "";

    const page =
      Number.isInteger(urlPage) && urlPage > 0 ? urlPage : DEFAULT_PAGE;

    const items =
      Number.isInteger(urlItemsPerPage) && urlItemsPerPage > 0
        ? urlItemsPerPage
        : DEFAULT_ITEMS_PER_PAGE;

    setActivePage(page);
    setItemsPerPage(items);
    setKeyword(urlKeyword);

    /**
     * 최초 접속해서 URL에 page가 없는 경우
     * 기본 페이징 정보를 URL에 설정
     */
    if (!params.get("page")) {
      params.set("page", `${page}`);
    }

    if (!params.get("items_per_page")) {
      params.set("items_per_page", `${items}`);
    }

    const newSearch = `?${params.toString()}`;

    if (location.search !== newSearch) {
      navigate(newSearch, {
        replace: true,
      });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    setIsPending(true);
    const qs = new URLSearchParams(location.search);
    if (activePage) {
      setKeyword(qs.get("keyword") || "");
      loadItems(activePage, qs.get("keyword") || "");
    }
  }, [loadItems, activePage, location?.search]);

  const handleSearch = useCallback(
    (value) => {
      console.log({ value });
      const params = new URLSearchParams(location.search);

      params.set("page", "1");

      if (value) {
        params.set("keyword", value);
      } else {
        params.delete("keyword");
      }

      navigate(`?${params.toString()}`, {
        replace: false,
      });
    },
    [location.search, navigate],
  );

  return (
    <DashboardLayout>
      <MDBox py={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              관제 센터
            </MDTypography>
          </MDBox>
          <MDBox pt={3} py={2} px={2}>
            <Grid container rowSpacing={1} sx={{ alignItems: "center" }}>
              <Grid item xs={12}>
                <MDBox
                  pt={3}
                  py={2}
                  px={2}
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={`${t("labels.all")} ${totalCount}`}
                    sx={{
                      fontWeight: 600,
                      "& .MuiChip-label": {
                        color: "#000",
                        fontWeight: 600,
                        px: 3,
                      },
                    }}
                  />
                  <Chip
                    label={`${t("labels.normal")} ${normalCount}`}
                    color="success"
                    sx={{
                      p: 0.5,
                      fontWeight: 600,
                      "& .MuiChip-label": {
                        color: "#fff",
                        fontWeight: 600,
                        px: 3,
                      },
                    }}
                  />
                  <Chip
                    label={`${t("labels.warning")} ${warningCount}`}
                    color="warning"
                    sx={{
                      fontWeight: 600,
                      "& .MuiChip-label": {
                        color: "#fff",
                        fontWeight: 600,
                        px: 3,
                      },
                    }}
                  />
                  <Chip
                    label={`${t("labels.offline")} ${offlineCount}`}
                    color="error"
                    sx={{
                      fontWeight: 600,
                      "& .MuiChip-label": {
                        color: "#fff",
                        fontWeight: 600,
                        px: 3,
                      },
                    }}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDBox
                  pt={3}
                  py={2}
                  px={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <MDBox
                    sx={{
                      flex: 1,
                      minWidth: 0,

                      // lg 이상
                      "@media (min-width: 1200px)": {
                        flex: "0 0 33.333%",
                        maxWidth: "33.333%",
                      },
                    }}
                  >
                    <SearchField
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      handleSearch={() => handleSearch(keyword)}
                    />
                  </MDBox>
                  <MDBox
                    sx={{
                      flex: "0 0 100px",
                      width: "100px",
                      minWidth: "100px",
                    }}
                  >
                    <SelectField
                      value={selectType}
                      onChange={(e) => {
                        setSelectType(e.target.value);
                      }}
                      valueOptions={[
                        { value: "all", label: t("labels.all") },
                        { value: "normal", label: t("labels.normal") },
                        { value: "warning", label: t("labels.warning") },
                        { value: "offline", label: t("labels.offline") },
                      ]}
                      isClearButton={false}
                    />
                  </MDBox>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table
                    sx={{ minWidth: 320, tableLayout: "fixed" }}
                    aria-label="simple table"
                  >
                    <TableHead sx={{ display: "contents" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            width: "80px",
                            minWidth: "80px",
                            maxWidth: "80px",
                            color: "inherit",
                            textAlign: "center",
                          }}
                        >
                          {t("table.rowNumber")}
                        </TableCell>
                        <TableCell sx={{ width: "40%", textAlign: "center" }}>
                          {t("table.stationName")}
                        </TableCell>
                        <TableCell sx={{ width: "10%", textAlign: "center" }}>
                          {t("table.statusCode")}
                        </TableCell>

                        <TableCell sx={{ width: "10%", textAlign: "center" }}>
                          {t("table.invOrder")}
                        </TableCell>
                        <TableCell sx={{ width: "30%", textAlign: "center" }}>
                          {t("table.alarams")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ boxSizing: "border-box" }}>
                      {isPending ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: "center" }}>
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : (
                        data?.items?.map((station, index) => {
                          const alarms = getAlarmRows(station);

                          const rowCount =
                            alarms.length > 0 ? alarms.length : 1;

                          const no =
                            data.totalItemCount -
                            ((data.page - 1) * data.pageSize + index);

                          // 알람이 없는 경우
                          if (alarms.length === 0) {
                            return (
                              <TableRow key={station.id}>
                                <TableCell
                                  sx={{
                                    width: 80,
                                    minWidth: 80,
                                    maxWidth: 80,
                                    textAlign: "center",
                                  }}
                                >
                                  {no}
                                </TableCell>

                                <TableCell sx={{ textAlign: "center" }}>
                                  {station.stationName}
                                </TableCell>

                                <TableCell sx={{ textAlign: "center" }}>
                                  {t(`labels.${station.statusCode}`)}
                                </TableCell>

                                <TableCell sx={{ textAlign: "center" }}>
                                  #
                                </TableCell>

                                <TableCell sx={{ textAlign: "center" }}>
                                  -
                                </TableCell>
                              </TableRow>
                            );
                          }

                          // 알람이 있는 경우
                          return alarms.map((item, alarmIndex) => (
                            <TableRow
                              key={`${station.id}-${item.invNo}-${alarmIndex}`}
                            >
                              {/* 순번 */}
                              {alarmIndex === 0 && (
                                <TableCell
                                  rowSpan={rowCount}
                                  sx={{
                                    width: 80,
                                    minWidth: 80,
                                    maxWidth: 80,
                                    textAlign: "center",
                                    verticalAlign: "top",
                                  }}
                                >
                                  {no}
                                </TableCell>
                              )}

                              {/* 발전소명 */}
                              {alarmIndex === 0 && (
                                <TableCell
                                  rowSpan={rowCount}
                                  sx={{
                                    textAlign: "center",
                                    verticalAlign: "top",
                                  }}
                                >
                                  {station.stationName}
                                </TableCell>
                              )}

                              {/* 상태 */}
                              {alarmIndex === 0 && (
                                <TableCell
                                  rowSpan={rowCount}
                                  sx={{
                                    textAlign: "center",
                                    verticalAlign: "top",
                                  }}
                                >
                                  {t(`labels.${station.statusCode}`)}
                                </TableCell>
                              )}

                              {/* 인버터 */}
                              <TableCell
                                sx={{
                                  textAlign: "center",
                                }}
                              >
                                #{item.invOrder}
                              </TableCell>

                              {/* 경보 */}
                              <TableCell
                                sx={{
                                  textAlign: "center",
                                }}
                              >
                                {item.alarms.map((alarm, alarmIdx) => (
                                  <MDTypography
                                    key={`${station.id}-${item.invNo}-${alarmIdx}`}
                                    variant="body2"
                                  >
                                    {t(`alarams.${alarm.alertCode}`)}
                                    {/* {
                                      consts.ALARM_CODE.find(
                                        (f) => f.value === alarm.alertCode,
                                      )?.label
                                    } */}
                                  </MDTypography>
                                ))}
                              </TableCell>
                            </TableRow>
                          ));
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                {data?.items && (
                  <Pagination
                    page={activePage}
                    count={
                      itemsPerPage
                        ? Math.ceil(data.totalItemCount / itemsPerPage)
                        : 0
                    }
                    onChange={(_, page) =>
                      changePaginations("activePage", page)
                    }
                  />
                )}
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default _;
